import React from 'react';
import { useParams } from 'react-router-dom';
import GenerateAIButton from '../../../../src/components/ui/remarks/generateAIButton';
import { SelectDropdown } from '../../../components/ui/dropdowns/SelectDropdown';
import { ErrorDisplay } from '../../../components/ui/error/ErrorDisplay';
import { AICalloutActionBanner } from '../../../components/ui/section/AICalloutActionBanner';
import { CustomLoadingSpinner } from '../../../components/ui/spinners/LoadingSpinner';
import { useCaseObservations } from '../../../hooks/useObservations';
import { AIRemarksContainer } from '../../remarks/components/aiRemarksContainer';
import { AnalysisRemarkKeys } from '../../remarks/config';


const INCIDENT_TYPE_OPTIONS = [
      'Fraud',
      'No Fraud',
      'Attempted Fraud',
    ].map(item => ({
    name: item,
    value: item
}));

const FRAUD_CATEGORY_OPTIONS = [
      'Collateral (Fake title deeds, spurious gold and/or its appraisal, FDRs, Bank Guarantees, Multiple mortgages)',
      'Documents (Fake invoices, LCs, transport documents, shipping bills, warehouse receipts, creation of charge in RC books, etc.)',
      'Financial Statements (Stock statements, inventory, debtors/creditors/receivables statements, fabricated balance sheets, manipulation of accounts, etc.)',
      'Related Party Transactions (Round tripping of funds, fake sales and purchases to inflate turnover)',
      'Clandestine sale of hypothecated/mortgaged security (Stocks, securities, machineries and equipment, vehicles, immovable property)',
      'Violations of FEMA / PMLA / Illegal claim of Input Credit in GST etc.',
      'Non-resident Accounts',
      'Fake KYC',
      'Overvaluation / Misrepresentation by Third Party Entities',
      'Cash Shortage - Cash Collection',
      'Cash Shortage - Embezzlement',
      'Cash Shortage - Manipulation',
      'Cash Shortage - Currency Chest',
      'Cheques / Demand Drafts - Fraudulent Encashment',
      'Cheques / Demand Drafts - Cloned / Forged Cheques',
      'Cheques / Demand Drafts - Demand Drafts',
      'Routing business transactions outside consortium / Non-routing through TRA & CC accounts',
      'Account Hacking / Compromise / Identity Theft',
      'Phishing',
      'Vishing',
      'Smishing',
      'Remote Capture of Device',
      'Lost / Stolen Device / Instrument',
      'Card Skimming',
      'SIM Swap',
      'Website Cloning / Fraudulent Link',
      'Fraudulent App',
      'Forgery / Modification of Payment',
      'Collect Payment Request',
      'Merchant Collusion',
      'New Type of Digital Frauds',
      'Others',
      'No Fraud',
    ].map(item => ({
    name: item,
    value: item
}));


const CaseObservationsContent: React.FC = () => {
  const { id: caseId } = useParams<{ id: string }>();
  const {
    observation,
    isLoading,
    error,
    onGenerate,
    onUpdateField,
    onImprove,
    handleRetry,
  } = useCaseObservations();

  if (!caseId) {
    return (
      <ErrorDisplay
        title="Case ID not found in URL"
        showRetryButton={false}
      />
    );
  }

  if (isLoading) {
    return (
      <CustomLoadingSpinner
        title="AI Agent is working..."
        subtitle="Please wait till the agent completes its tasks."
        minHeight="min-h-[400px]"
      />
    );
  }

  if (error) {
    return (
      <ErrorDisplay
        title="Failed to Load Observations"
        error={error}
        onRetry={handleRetry}
      />
    );
  }

  if (!observation) {
    return (
      <ErrorDisplay
        title="No Observation Data"
        error="No observation data could be found for this case. You can try fetching the data."
        onRetry={handleRetry}
      />
    );
  }

  return (
    <>
      {/* <div className="flex justify-end">
        <Button
          variant="ai"
          size="sm"
          onClick={onGenerate}
          disabled={isLoading}
          title="Observations"
          className="gap-2 text-base font-semibold"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          Generate with AI
        </Button>
      </div> */}

      <AICalloutActionBanner
        title="Fraud Mechanism and Systemic Gaps"
        description="Let the Canso AI agent analyse case details and draft initial observations for you."
      >
        <GenerateAIButton onClick={onGenerate} disabled={isLoading}   loading={isLoading} className='gap-2 text-base font-semibold'/>
     </AICalloutActionBanner>

      {/* Modus Operandi Section */}
      <div className="mt-1">
        <AIRemarksContainer
          remarkKey={AnalysisRemarkKeys.ModusOperandi}
          initialValue={observation.modus_operandi?.value || ''}
          onSave={(newText) => onUpdateField('modus_operandi', newText)}
          onImprove={onImprove}
          onGenerate={onGenerate}
          disableGenerate={true}
        />
      </div>

      {/* Lapses Identified Section */}
      <div className="mt-4">
        <div className="flex items-center gap-2 mb-3 p-2 bg-gray-50 border rounded-lg">
          <input
            type="checkbox"
            id="lapses-identified-toggle"
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={!!observation.lapses_identified?.value}
            onChange={(e) => {
              const newValue = e.target.checked ? 'true' : '';
              onUpdateField('lapses_identified', newValue);
            }}
          />
          <label htmlFor="lapses-identified-toggle" className="font-medium text-sm text-gray-800">
            Lapses Identified
          </label>
        </div>
        
        <AIRemarksContainer
          remarkKey={AnalysisRemarkKeys.Lapses}
          initialValue={observation.lapse_root_cause?.value || ''}
          onSave={(newText) => onUpdateField('lapse_root_cause', newText)}
          onImprove={onImprove}
          onGenerate={onGenerate}
          disableGenerate={true}
        />
      </div>

      {/* Dropdowns Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 mt-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Incident Type
          </label>
          <SelectDropdown
            value={observation.incident_type?.value || ''}
            options={INCIDENT_TYPE_OPTIONS}
            placeholder="Select incident type..."
            allowClear={false}
            onChange={(value) => onUpdateField('incident_type', value)}
          />
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Type of Fraud
          </label>
          <SelectDropdown
            value={observation.fraud_category?.value || ''}
            options={FRAUD_CATEGORY_OPTIONS}
            placeholder="Select Type of fraud"
            allowClear={false}
            wrapText={true}
            onChange={(value) => onUpdateField('fraud_category', value)}
          />
        </div>
      </div>

      {/* Additional Observations Section */}
      <div className="mt-4">
        <AIRemarksContainer
          remarkKey={AnalysisRemarkKeys.AdditionalDetails}
          initialValue={observation.additional_observations?.value || ''}
          onSave={(newText) => onUpdateField('additional_observations', newText)}
          onImprove={onImprove}
          onGenerate={onGenerate}
          disableGenerate={true}
          // disableGenerate={!!observation.additional_observations?.value}
        />
      </div>
    </>
  );
};

export default CaseObservationsContent;
